<div class="page-container dashboard-modern">
  <div
    class="page-header flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4"
  >
    <div>
      <h1 class="page-title text-xl font-bold text-slate-900 dark:text-white">
        Dashboard Canal Partners
      </h1>
      <p class="page-subtitle text-xs sm:text-sm text-slate-500 dark:text-slate-400">
        Resumen general del rendimiento de tu canal de partners.
      </p>
    </div>

    <div class="flex items-center gap-3 w-full md:w-auto">
      <app-date-range-picker
        class="w-full md:w-auto"
        [startDate]="startDate()"
        [endDate]="endDate()"
        (rangeChange)="onDateRangeChange($event)"
        placeholder="Seleccionar rango"
      ></app-date-range-picker>
    </div>
  </div>

  @if (isLoading()) {
    <div class="loading-state flex flex-col items-center justify-center py-24">
      <div class="loading-spinner"></div>
      <p class="mt-4 text-slate-500 dark:text-slate-400 font-medium">Cargando dashboard...</p>
    </div>
  } @else {
    <!-- KPI Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
      <!-- Pipeline Total -->
      <div class="kpi-card">
        <div class="kpi-icon kpi-icon--blue">
          <fa-icon [icon]="faEuroSign"></fa-icon>
        </div>
        <div class="kpi-content">
          <div class="kpi-label">PIPELINE TOTAL (€)</div>
          <div class="kpi-value">{{ formatCurrency(metrics()?.amounts?.in_progress) }}</div>
          <div class="kpi-footer">Suma de importes en estado En curso</div>
        </div>
      </div>

      <!-- Ventas Cerradas -->
      <div class="kpi-card">
        <div class="kpi-icon kpi-icon--green">
          <fa-icon [icon]="faChartLineUp"></fa-icon>
        </div>
        <div class="kpi-content">
          <div class="kpi-label">VENTAS CERRADAS (€)</div>
          <div class="kpi-value">{{ formatCurrency(metrics()?.amounts?.won) }}</div>
          <div class="kpi-footer">Suma de importes en estado Ganada</div>
        </div>
      </div>

      <!-- Oportunidades Activas -->
      <div class="kpi-card">
        <div class="kpi-icon kpi-icon--purple">
          <fa-icon [icon]="faFolderOpen"></fa-icon>
        </div>
        <div class="kpi-content">
          <div class="kpi-label">OPORTUNIDADES ACTIVAS</div>
          <div class="kpi-value">{{ metrics()?.active_total ?? 0 }}</div>
          <div class="kpi-footer">Oportunidades en estado En curso</div>
        </div>
      </div>

      <!-- % Conversión -->
      <div class="kpi-card">
        <div class="kpi-icon kpi-icon--orange">
          <fa-icon [icon]="faFilter"></fa-icon>
        </div>
        <div class="kpi-content">
          <div class="kpi-label">% CONVERSIÓN</div>
          <div class="kpi-value">{{ formatPercent(metrics()?.conversion_rate) }}</div>
          <div class="kpi-footer">Ganadas / (Ganadas + Perdidas)</div>
        </div>
      </div>
    </div>

    @if (contractStatusService.hasPendingSignature()) {
      <!-- Aviso de contrato pendiente de firma -->
      <a routerLink="/mi-ficha" class="alert-info mb-6">
        <fa-icon [icon]="faCircleInfo"></fa-icon>
        <p>
          Tienes un contrato pendiente de firma. Por favor,
          <span class="underline font-semibold">revisa tu contrato en Mi Ficha</span>
          para firmarlo.
        </p>
      </a>
    }

    <!-- Middle Section: Pipeline State & Partner Type Summary -->
    <div class="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
      <!-- Resumen por Estado -->
      <div class="widget-card" [class.xl:col-span-2]="!isAdmin">
        <h3 class="widget-title">
          RESUMEN POR ESTADO
          <span class="text-xs text-slate-400 font-normal">(Nº OPORTUNIDADES E IMPORTE)</span>
        </h3>
        <div class="h-56" echarts [options]="donutOptions()" [theme]="echartsTheme()"></div>
        <div class="grid grid-cols-3 gap-3 mt-2">
          <div class="summary-item">
            <div class="summary-label">EN CURSO</div>
            <div class="summary-value text-blue-600">
              {{ formatCurrency(metrics()?.amounts?.in_progress) }}
            </div>
            <div class="summary-footer">Pipeline abierto</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">GANADAS</div>
            <div class="summary-value text-emerald-500">
              {{ formatCurrency(metrics()?.amounts?.won) }}
            </div>
            <div class="summary-footer">Ingresos cerrados</div>
          </div>
          <div class="summary-item">
            <div class="summary-label">PERDIDAS</div>
            <div class="summary-value text-slate-400">
              @if (metrics()?.amounts?.lost) {
                {{ formatCurrency(metrics()?.amounts?.lost) }}
              } @else {
                <span class="opacity-40">-</span>
              }
            </div>
            <div class="summary-footer">Sin importe disponible</div>
          </div>
        </div>
      </div>

      @if (isAdmin) {
        <!-- Resumen por Tipo de Partner -->
        <div class="widget-card">
          <h3 class="widget-title">
            RESUMEN POR TIPO DE PARTNER
            <span class="text-xs text-slate-400 font-normal">(Nº OPORTUNIDADES E IMPORTE)</span>
          </h3>
          <div
            class="h-72"
            echarts
            [options]="partnerTypeBarOptions()"
            [theme]="echartsTheme()"
          ></div>
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            @for (opt of partnerTypeOptions; track opt.value) {
              <div class="summary-item !p-2">
                <div class="summary-label !text-[10px] truncate" [title]="opt.label">
                  {{ opt.label }}
                </div>
                <div class="summary-value !text-sm" [style.color]="partnerTypeColors[opt.value]">
                  {{ formatCurrency(metrics()?.amount_by_type?.[opt.value]) }}
                </div>
              </div>
            }
          </div>
        </div>
      }
    </div>

    <!-- Bottom Section: Top Partners & Channel Activation -->
    @if (showTopPartners() || showChannelActivation()) {
      <div
        class="grid grid-cols-1 gap-4 mb-6"
        [class.xl:grid-cols-2]="showTopPartners() && showChannelActivation()"
      >
        <!-- Top Partners Table -->
        @if (showTopPartners()) {
          <div class="widget-card">
            <h3 class="widget-title">TOP PARTNERS POR PIPELINE</h3>
            <div class="overflow-x-auto mt-4">
              <table class="w-full text-left text-sm">
                <thead>
                  <tr
                    class="border-b border-slate-100 dark:border-slate-800 text-slate-400 uppercase text-xs"
                  >
                    <th class="pb-3 font-medium">#</th>
                    <th class="pb-3 font-medium">Partner</th>
                    <th class="pb-3 font-medium text-center">Nº Oportunidades</th>
                    <th class="pb-3 font-medium text-right">Pipeline (€)</th>
                    <th class="pb-3 font-medium text-right">Ganadas (€)</th>
                  </tr>
                </thead>
                <tbody>
                  @for (partner of metrics()?.top_partners; track partner.id; let i = $index) {
                    <tr class="border-b border-slate-50 dark:border-slate-800/50 last:border-0">
                      <td class="py-3 text-slate-500">{{ i + 1 }}</td>
                      <td class="py-3 font-medium text-slate-700 dark:text-slate-200">
                        {{ partner.name }}
                      </td>
                      <td class="py-3 text-center">{{ partner.opportunity_count }}</td>
                      <td class="py-3 text-right font-semibold">
                        {{ formatCurrency(partner.pipeline_amount) }}
                      </td>
                      <td class="py-3 text-right text-emerald-500 font-semibold">
                        {{ formatCurrency(partner.won_amount) }}
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          </div>
        }

        <!-- Channel Activation -->
        @if (showChannelActivation()) {
          <div class="widget-card">
            <h3 class="widget-title">ACTIVACIÓN DEL CANAL</h3>
            <div class="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
              <div class="activation-card">
                <fa-icon [icon]="faUsers" class="text-blue-500 text-xl mb-3"></fa-icon>
                <div class="text-2xl font-bold">
                  {{ metrics()?.channel_activation?.total ?? 0 }}
                </div>
                <div class="text-xs text-slate-600 dark:text-slate-400">Partners totales</div>
                <div class="text-[10px] text-slate-400 mt-1">Total registrados</div>
              </div>
              <div class="activation-card">
                <fa-icon [icon]="faCircleCheck" class="text-emerald-500 text-xl mb-3"></fa-icon>
                <div class="text-2xl font-bold">
                  {{ metrics()?.channel_activation?.with_deals ?? 0 }}
                </div>
                <div class="text-xs text-slate-600 dark:text-slate-400">Con ≥ 1 oportunidad</div>
                <div class="text-[10px] text-slate-400 mt-1">
                  {{
                    formatPercent(
                      ((metrics()?.channel_activation?.with_deals ?? 0) /
                        (metrics()?.channel_activation?.total || 1)) *
                        100
                    )
                  }}
                  del total
                </div>
              </div>
              <div class="activation-card">
                <fa-icon [icon]="faStar" class="text-orange-400 text-xl mb-3"></fa-icon>
                <div class="text-2xl font-bold">
                  {{ metrics()?.channel_activation?.with_three_plus ?? 0 }}
                </div>
                <div class="text-xs text-slate-600 dark:text-slate-400">Con ≥ 3 oportunidades</div>
                <div class="text-[10px] text-slate-400 mt-1">
                  {{
                    formatPercent(
                      ((metrics()?.channel_activation?.with_three_plus ?? 0) /
                        (metrics()?.channel_activation?.total || 1)) *
                        100
                    )
                  }}
                  del total
                </div>
              </div>
              <div class="activation-card">
                <fa-icon [icon]="faUserSlash" class="text-red-400 text-xl mb-3"></fa-icon>
                <div class="text-2xl font-bold">
                  {{ metrics()?.channel_activation?.inactive ?? 0 }}
                </div>
                <div class="text-xs text-slate-600 dark:text-slate-400">Sin oportunidades</div>
                <div class="text-[10px] text-slate-400 mt-1">
                  {{
                    formatPercent(
                      ((metrics()?.channel_activation?.inactive ?? 0) /
                        (metrics()?.channel_activation?.total || 1)) *
                        100
                    )
                  }}
                  del total
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    }

    <!-- Top Partners by Sales & Opportunities -->
    @if (showTopPartnersBySales() || showTopPartnersByOpportunities()) {
      <div
        class="grid grid-cols-1 gap-4 mb-6"
        [class.xl:grid-cols-2]="showTopPartnersBySales() && showTopPartnersByOpportunities()"
      >
        <!-- Top Partners by Sales -->
        @if (showTopPartnersBySales()) {
          <div class="widget-card">
            <h3 class="widget-title uppercase tracking-wider text-xs font-bold text-slate-400 mb-4">
              Top Partners por Ventas (€)
            </h3>
            <div class="space-y-3">
              @for (partner of metrics()?.top_partners_by_sales; track partner.id; let i = $index) {
                <div
                  class="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <div
                    class="w-6 h-6 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-[10px] font-bold text-emerald-600"
                  >
                    {{ i + 1 }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                      {{ partner.name }}
                    </div>
                  </div>
                  <div class="text-sm font-bold text-emerald-500">
                    {{ formatCurrency(partner.value) }}
                  </div>
                </div>
              }
            </div>
          </div>
        }

        <!-- Top Partners by Opportunities -->
        @if (showTopPartnersByOpportunities()) {
          <div class="widget-card">
            <h3 class="widget-title uppercase tracking-wider text-xs font-bold text-slate-400 mb-4">
              Top Partners por OPS Activas
            </h3>
            <div class="space-y-3">
              @for (
                partner of metrics()?.top_partners_by_opportunities;
                track partner.id;
                let i = $index
              ) {
                <div
                  class="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                >
                  <div
                    class="w-6 h-6 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-[10px] font-bold text-blue-600"
                  >
                    {{ i + 1 }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-slate-700 dark:text-slate-200 truncate">
                      {{ partner.name }}
                    </div>
                    <div class="text-[10px] text-slate-400 mt-0.5">
                      Importe: {{ formatCurrency(partner.value) }}
                    </div>
                  </div>
                  <div class="text-sm font-bold text-blue-500">
                    {{ partner.count }}
                    <span class="text-[10px] text-slate-400 font-normal ml-1">ops</span>
                  </div>
                </div>
              }
            </div>
          </div>
        }
      </div>
    }

    <!-- Temporal Evolution -->
    <div class="widget-card mb-6">
      <div class="flex justify-between items-start mb-6">
        <h3 class="widget-title">
          EVOLUCIÓN TEMPORAL
          <span class="text-xs text-slate-400 font-normal">(Nº DE OPORTUNIDADES)</span>
        </h3>
      </div>
      <div class="grid grid-cols-1 lg:grid-cols-4 gap-4 items-center">
        <div
          class="lg:col-span-3 h-64"
          echarts
          [options]="lineOptions()"
          [theme]="echartsTheme()"
        ></div>
        <div class="space-y-4">
          <div
            class="bg-blue-50/50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100 dark:border-blue-900/30"
          >
            <div class="flex justify-between items-start">
              <div class="text-2xl font-bold text-blue-600">{{ metrics()?.total ?? 0 }}</div>
            </div>
            <div class="text-xs font-medium mt-1">Oportunidades creadas</div>
            <div class="text-[10px] text-slate-500 mt-1">Últimos meses</div>
          </div>
          <div
            class="bg-emerald-50/50 dark:bg-emerald-900/10 p-4 rounded-xl border border-emerald-100 dark:border-emerald-900/30"
          >
            <div class="flex justify-between items-start">
              <div class="text-2xl font-bold text-emerald-500">
                {{ (metrics()?.by_state?.won ?? 0) + (metrics()?.by_state?.lost ?? 0) }}
              </div>
            </div>
            <div class="text-xs font-medium mt-1">Oportunidades cerradas</div>
            <div class="text-[10px] text-slate-500 mt-1">Últimos meses</div>
          </div>
        </div>
      </div>
    </div>

    @if (errorMessage()) {
      <div class="error-banner">
        {{ errorMessage() }}
      </div>
    }
  }
</div>
